// CompareQualityView - Quality comparison with corporate design
import { h } from 'preact'
import { useState, useEffect, useRef } from 'preact/hooks'
import * as echarts from 'echarts'
import { compareCells, hiddenCells } from '../../../state/filterStore.js'
import { getBatchCurves, getCellFromExplorerCache } from '../../../modules/dataService.js'
import { QUALITY } from '../../../data/definitions.js'
import { IconActivity, IconClock } from '../../shared/Icons.jsx'
import LockedMetricToken from '../../shared/LockedMetricToken.jsx'

// Cell colors for comparison
const CELL_COLORS = [
  '#ea5b0c', // Orange (primary)
  '#0d7c99', // Cyan
  '#88a2aa', // Gray-blue
  '#ff8c42', // Light orange
  '#0a4d68'  // Dark cyan
]

// Custom Icons for Quality metrics
const CapacityIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="16" height="10" rx="2" ry="2"></rect>
    <line x1="22" y1="11" x2="22" y2="13"></line>
    <line x1="6" y1="11" x2="10" y2="11"></line>
  </svg>
)

const ImpedanceIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
  </svg>
)

// Determine quality indicator class
const getQualityClass = (value, type) => {
  if (value == null || isNaN(value) || value === 0) return ''
  
  if (type === 'variation') {
    const pct = value * 100
    if (pct < 0.5) return 'quality-excellent'
    if (pct < 1.5) return 'quality-good'
    if (pct < 3) return 'quality-moderate'
    return 'quality-poor'
  }
  
  if (type === 'aging') {
    if (value <= 0) return 'quality-excellent'
    const pct = value * 100
    if (pct < 1) return 'quality-good'
    if (pct < 3) return 'quality-moderate'
    return 'quality-poor'
  }
  
  return ''
}

export default function CompareQualityView({ view = 'table' }) {
  const [cellsData, setCellsData] = useState([])
  const [loading, setLoading] = useState(true)
  const [isFlipped, setIsFlipped] = useState({ variation: false, aging: false })
  
  const capPlotRef = useRef(null)
  const eisPlotRef = useRef(null)
  const capChartInstance = useRef(null)
  const eisChartInstance = useRef(null)

  // Load cell data
  useEffect(() => {
    async function loadCells() {
      const cellIds = compareCells.value
      if (!cellIds || cellIds.length === 0) {
        setLoading(false)
        setCellsData([])
        return
      }

      setLoading(true)
      try {
        // Use batch-curves for array data + aging scalars (1 request instead of N)
        const result = await getBatchCurves(cellIds, ['batch_spread', 'aging'])

        const data = cellIds.map(productId => {
          const batchData = result.cells?.[productId]?.batch_spread || {}
          const agingData = result.cells?.[productId]?.aging || {}
          const meta = result.metadata?.[productId] || {}
          // Try explorer cache for identity fields
          const cached = getCellFromExplorerCache(productId)
          const manufacturer = cached?.manufacturer || meta.manufacturer || 'Unknown'
          const name = cached?.name || meta.name || 'Unknown'
          // Bug C: backend gates aging/batch_spread wholesale ({gated:true}) for
          // non-demo cells in a mixed DEMO batch. Render a lock, not a bare "—".
          const premiumLocked = !!(batchData.gated || agingData.gated)

          return {
            productId,
            name: `${manufacturer} ${name}`,
            manufacturer,
            cellName: name,
            premiumLocked,
            cellData: {
              batch: batchData,
              aging: agingData,
              manufacturer,
              name
            }
          }
        })

        setCellsData(data.filter(Boolean))
      } catch (err) {
        console.error('[CompareQualityView] Failed to load cells:', err)
        // A DEMO_LIMIT-shaped error is already surfaced by the central
        // conversion overlay (WS 1.1) — don't ALSO blow away the cart with a
        // false empty-state on top of it. Other errors stay graceful: leave
        // cellsData as-is (no cart loss) rather than clearing it to [].
        if (err?.code !== 'DEMO_LIMIT' && err?.detail?.code !== 'demo_daily_view_limit') {
          setCellsData([])
        }
      } finally {
        setLoading(false)
      }
    }

    loadCells()
  }, [compareCells.value])

  const visibleCellsData = cellsData.filter(cell => !hiddenCells.value.includes(cell.productId))

  // Render Capacity Spread Plot
  useEffect(() => {
    if (!capPlotRef.current || visibleCellsData.length === 0) return

    if (capChartInstance.current) capChartInstance.current.dispose()
    capChartInstance.current = echarts.init(capPlotRef.current)

    const maxBatchSize = Math.max(...visibleCellsData.map(c => 
      c.cellData?.batch?.CAP_spread?.length || 0
    ))

    if (maxBatchSize === 0) {
      capChartInstance.current.clear()
      return
    }

    const cellNumbers = Array.from({ length: maxBatchSize }, (_, i) => `${i + 1}`)
    const series = []
    
    visibleCellsData.forEach((cell, idx) => {
      const batch = cell.cellData?.batch
      if (!batch || !batch.CAP_spread || batch.CAP_spread.length === 0) return

      const mean = batch.CAP_spread_mean || 0
      const deviations = batch.CAP_spread.map(val => ((val - mean) / mean) * 100)

      series.push({
        type: 'scatter',
        name: cell.cellName,
        data: deviations,
        symbolSize: 8,
        itemStyle: { color: CELL_COLORS[idx % CELL_COLORS.length] }
      })

      series.push({
        type: 'line',
        data: deviations.map(() => 0),
        lineStyle: { color: CELL_COLORS[idx % CELL_COLORS.length], width: 1, type: 'dashed', opacity: 0.3 },
        symbol: 'none',
        silent: true
      })
    })

    const option = {
      backgroundColor: 'transparent',
      grid: { left: 40, right: 10, top: 8, bottom: 25 },
      legend: { show: false },
      xAxis: {
        type: 'category',
        data: cellNumbers,
        axisLine: { lineStyle: { color: 'rgba(255,255,255,0.1)' } },
        axisLabel: { color: '#666', fontSize: 9, fontFamily: 'Roboto' },
        splitLine: { show: false }
      },
      yAxis: {
        type: 'value',
        name: '%',
        nameLocation: 'end',
        nameGap: 5,
        nameTextStyle: { color: '#666', fontSize: 9, fontFamily: 'Roboto' },
        axisLine: { show: false },
        axisLabel: { color: '#666', fontSize: 9, fontFamily: 'Roboto', formatter: v => v.toFixed(1) },
        splitLine: { lineStyle: { color: 'rgba(255,255,255,0.04)' } }
      },
      series,
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(35,32,28,0.95)',
        borderColor: '#ea5b0c',
        textStyle: { color: '#fff', fontSize: 11, fontFamily: 'Roboto' },
        formatter: p => p.seriesType !== 'scatter' ? '' : 
          `<b style="font-family:Big John">${p.seriesName}</b><br/>Cell ${p.dataIndex + 1}: ${p.value >= 0 ? '+' : ''}${p.value.toFixed(2)}%`
      }
    }

    capChartInstance.current.setOption(option)
    const ro = new ResizeObserver(() => capChartInstance.current?.resize())
    ro.observe(capPlotRef.current)
    return () => ro.disconnect()
  }, [visibleCellsData])

  // Render Impedance Spread Plot
  useEffect(() => {
    if (!eisPlotRef.current || visibleCellsData.length === 0) return

    if (eisChartInstance.current) eisChartInstance.current.dispose()
    eisChartInstance.current = echarts.init(eisPlotRef.current)

    const maxBatchSize = Math.max(...visibleCellsData.map(c => 
      c.cellData?.batch?.EIS_spread?.length || 0
    ))

    if (maxBatchSize === 0) {
      eisChartInstance.current.clear()
      return
    }

    const cellNumbers = Array.from({ length: maxBatchSize }, (_, i) => `${i + 1}`)
    const series = []
    
    visibleCellsData.forEach((cell, idx) => {
      const batch = cell.cellData?.batch
      if (!batch || !batch.EIS_spread || batch.EIS_spread.length === 0) return

      const mean = batch.EIS_spread_mean || 0
      const deviations = batch.EIS_spread.map(val => ((val - mean) / mean) * 100)

      series.push({
        type: 'scatter',
        name: cell.cellName,
        data: deviations,
        symbolSize: 8,
        itemStyle: { color: CELL_COLORS[idx % CELL_COLORS.length] }
      })

      series.push({
        type: 'line',
        data: deviations.map(() => 0),
        lineStyle: { color: CELL_COLORS[idx % CELL_COLORS.length], width: 1, type: 'dashed', opacity: 0.3 },
        symbol: 'none',
        silent: true
      })
    })

    const option = {
      backgroundColor: 'transparent',
      grid: { left: 40, right: 10, top: 8, bottom: 25 },
      legend: { show: false },
      xAxis: {
        type: 'category',
        data: cellNumbers,
        axisLine: { lineStyle: { color: 'rgba(255,255,255,0.1)' } },
        axisLabel: { color: '#666', fontSize: 9, fontFamily: 'Roboto' },
        splitLine: { show: false }
      },
      yAxis: {
        type: 'value',
        name: '%',
        nameLocation: 'end',
        nameGap: 5,
        nameTextStyle: { color: '#666', fontSize: 9, fontFamily: 'Roboto' },
        axisLine: { show: false },
        axisLabel: { color: '#666', fontSize: 9, fontFamily: 'Roboto', formatter: v => v.toFixed(1) },
        splitLine: { lineStyle: { color: 'rgba(255,255,255,0.04)' } }
      },
      series,
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(35,32,28,0.95)',
        borderColor: '#0d7c99',
        textStyle: { color: '#fff', fontSize: 11, fontFamily: 'Roboto' },
        formatter: p => p.seriesType !== 'scatter' ? '' : 
          `<b style="font-family:Big John">${p.seriesName}</b><br/>Cell ${p.dataIndex + 1}: ${p.value >= 0 ? '+' : ''}${p.value.toFixed(2)}%`
      }
    }

    eisChartInstance.current.setOption(option)
    const ro = new ResizeObserver(() => eisChartInstance.current?.resize())
    ro.observe(eisPlotRef.current)
    return () => ro.disconnect()
  }, [visibleCellsData])

  // Extract stats
  const cellStats = visibleCellsData.map((cell, idx) => {
    const batch = cell.cellData?.batch || {}
    const aging = cell.cellData?.aging || {}
    
    return {
      name: cell.cellName,
      manufacturer: cell.manufacturer,
      color: CELL_COLORS[idx % CELL_COLORS.length],
      premiumLocked: !!cell.premiumLocked,
      capMean: batch.CAP_spread_mean || 0,
      capStdAbs: batch.CAP_spread_std_abs || 0,
      capStdRel: batch.CAP_spread_std_rel || 0,
      eisMean: batch.EIS_spread_mean || 0,
      eisStdAbs: batch.EIS_spread_std_abs || 0,
      eisStdRel: batch.EIS_spread_std_rel || 0,
      capLoss: aging.CAP_loss || 0,
      capLossRel: aging.CAP_loss_rel || 0,
      eisIncrease: aging.EIS_increase || 0,
      eisIncreaseRel: aging.EIS_increase_rel || 0
    }
  })

  // Find best values
  const findBest = (key) => {
    const values = cellStats.map(s => Math.abs(s[key])).filter(v => v !== 0)
    if (values.length === 0) return null
    const best = Math.min(...values)
    return cellStats.findIndex(s => Math.abs(s[key]) === best)
  }

  const bestCapVariation = findBest('capStdRel')
  const bestEisVariation = findBest('eisStdRel')
  const bestCapLoss = findBest('capLossRel')
  const bestEisIncrease = findBest('eisIncreaseRel')

  // Determine if index is worst (highest value for relative metrics)
  const findWorst = (key) => {
    const values = cellStats.map(s => Math.abs(s[key])).filter(v => v !== 0)
    if (values.length === 0) return null
    const worst = Math.max(...values)
    return cellStats.findIndex(s => Math.abs(s[key]) === worst)
  }

  const worstCapVariation = findWorst('capStdRel')
  const worstEisVariation = findWorst('eisStdRel')
  const worstCapLoss = findWorst('capLossRel')
  const worstEisIncrease = findWorst('eisIncreaseRel')

  // Get quality class for value row (best=green arrow up, worst=red arrow down)
  const getQualityRowClass = (idx, bestIdx, worstIdx) => {
    if (idx === bestIdx) return 'highlight-best'
    if (idx === worstIdx) return 'highlight-worst'
    return ''
  }

  const toggleFlip = (section) => {
    setIsFlipped(prev => ({ ...prev, [section]: !prev[section] }))
  }

  if (loading) {
    return (
      <div className="compare-section-view loading">
        <div className="loading-spinner">Loading quality data...</div>
      </div>
    )
  }

  // Real empty cart — the only case that should ever say "no cells". Never key
  // this on cellsData.length: gated/failed loads must not masquerade as an
  // empty cart (Bug C false empty-state).
  if (compareCells.value.length === 0) {
    return (
      <div className="compare-section-view empty">
        <div className="empty-state">
          <p>No cells selected for comparison</p>
        </div>
      </div>
    )
  }

  if (cellsData.length === 0) {
    // Cart has cells but none loaded yet — still loading, or the request
    // failed/hit a DEMO_LIMIT gate that WS 1.1's central overlay already
    // surfaces. Never claim "no cells" here.
    return (
      <div className="compare-section-view loading">
        <div className="loading-spinner">Preparing quality comparison…</div>
      </div>
    )
  }

  if (visibleCellsData.length === 0) {
    return (
      <div className="compare-section-view empty">
        <div className="empty-state">
          <p>All cells are hidden</p>
        </div>
      </div>
    )
  }

  // Render value with split number and unit (fact sheet style)
  const renderValue = (value, unit, decimals = 3, showSign = false) => {
    if (value == null || isNaN(value)) return <span className="value-num">N/A</span>
    const num = value.toFixed(decimals)
    const displayNum = showSign && value > 0 ? `+${num}` : num
    return (
      <>
        <span className="value-num">{displayNum}</span>
        <span className="value-unit">{unit}</span>
      </>
    )
  }

  // Render percentage value (only show minus for negative, no plus for positive)
  const renderPercent = (value, showSign = false) => {
    if (value == null || isNaN(value)) return <span className="value-num">N/A</span>
    const pct = (value * 100).toFixed(2)
    // showSign ignored - always show value without plus sign
    return (
      <>
        <span className="value-num">{pct}</span>
        <span className="value-unit">%</span>
      </>
    )
  }

  return (
    <div className="compare-section-view compare-quality-layout">
      {/* Cell-to-Cell Variation Card */}
      <div className={`compare-quality-card flipcard ${isFlipped.variation ? 'flipped' : ''}`}>
        <div className="flipcard-inner">
          <div className="flipcard-front compare-flipcard-front">
            <button className="flip-btn" onClick={() => toggleFlip('variation')} title="Show info">?</button>
            
            <div className="quality-card-header">
              <div className="header-icon"><IconActivity size={20} /></div>
              <div className="header-text">
                <h3 className="card-title">Cell-to-Cell Variation</h3>
                <p className="card-subtitle">Manufacturing consistency</p>
              </div>
            </div>
            
            {/* Capacity Section */}
            <div className="quality-metric-section">
              <div className="metric-header">
                <CapacityIcon size={14} />
                <span>Capacity Spread</span>
              </div>
              <div className="quality-plot-mini" ref={capPlotRef}></div>

              {/* Cell Legend */}
              <div className="quality-cell-legend">
                {cellStats.map((s, idx) => (
                  <div key={idx} className="legend-item">
                    <span className="legend-dot" style={`background: ${s.color}`}></span>
                    <span className="legend-name">{s.name}</span>
                  </div>
                ))}
              </div>
              
              <table className="quality-stats-compact">
                <thead>
                  <tr>
                    <th>Cell</th>
                    <th>Mean</th>
                    <th>σ<sub>rel</sub> (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {cellStats.map((s, idx) => (
                    <tr key={idx} className={s.premiumLocked ? '' : getQualityRowClass(idx, bestCapVariation, worstCapVariation)}>
                      <td className="cell-name-col">{s.manufacturer}<br />{s.name}</td>
                      {s.premiumLocked ? (
                        <td className="metric-col" colSpan={2}>
                          <LockedMetricToken gated unit="Ah" category="quality" />
                        </td>
                      ) : (
                        <>
                          <td className="metric-col">
                            <span className="stat-value">{renderValue(s.capMean, 'Ah')}</span>
                          </td>
                          <td className="metric-col">
                            <span className="stat-value">
                              {renderPercent(s.capStdRel)}
                            </span>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Impedance Section */}
            <div className="quality-metric-section">
              <div className="metric-header">
                <ImpedanceIcon size={14} />
                <span>Impedance Spread</span>
              </div>
              <div className="quality-plot-mini" ref={eisPlotRef}></div>

              {/* Cell Legend */}
              <div className="quality-cell-legend">
                {cellStats.map((s, idx) => (
                  <div key={idx} className="legend-item">
                    <span className="legend-dot" style={`background: ${s.color}`}></span>
                    <span className="legend-name">{s.name}</span>
                  </div>
                ))}
              </div>
              
              <table className="quality-stats-compact">
                <thead>
                  <tr>
                    <th>Cell</th>
                    <th>Mean</th>
                    <th>σ<sub>rel</sub> (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {cellStats.map((s, idx) => (
                    <tr key={idx} className={s.premiumLocked ? '' : getQualityRowClass(idx, bestEisVariation, worstEisVariation)}>
                      <td className="cell-name-col">{s.manufacturer}<br />{s.name}</td>
                      {s.premiumLocked ? (
                        <td className="metric-col" colSpan={2}>
                          <LockedMetricToken gated unit="mΩ" category="quality" />
                        </td>
                      ) : (
                        <>
                          <td className="metric-col">
                            <span className="stat-value">{renderValue(s.eisMean, 'mΩ')}</span>
                          </td>
                          <td className="metric-col">
                            <span className="stat-value">
                              {renderPercent(s.eisStdRel)}
                            </span>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Back - Definition */}
          <div className="flipcard-back compare-flipcard-back">
            <button className="flip-btn flip-btn-back" onClick={() => toggleFlip('variation')} title="Close">×</button>
            <h3 className="card-title">{QUALITY.batchVariation.title}</h3>
            <div className="flip-content">
              <p>{QUALITY.batchVariation.intro}</p>
              <h4>{QUALITY.batchVariation.capacity.title}</h4>
              <p>{QUALITY.batchVariation.capacity.text.replace(/\{[^}]+\}/g, '')}</p>
              <h4>{QUALITY.batchVariation.impedance.title}</h4>
              <p>{QUALITY.batchVariation.impedance.text.replace(/\{[^}]+\}/g, '')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Post-Formation Loss Card */}
      <div className={`compare-quality-card flipcard ${isFlipped.aging ? 'flipped' : ''}`}>
        <div className="flipcard-inner">
          <div className="flipcard-front compare-flipcard-front">
            <button className="flip-btn" onClick={() => toggleFlip('aging')} title="Show info">?</button>
            
            <div className="quality-card-header">
              <div className="header-icon"><IconClock size={20} /></div>
              <div className="header-text">
                <h3 className="card-title">Post-Formation Loss</h3>
                <p className="card-subtitle">Initial aging after formation</p>
              </div>
            </div>

            {/* Capacity Loss Section */}
            <div className="quality-metric-section">
              <div className="metric-header">
                <CapacityIcon size={14} />
                <span>Capacity Loss</span>
              </div>

              {/* Cell Legend */}
              <div className="quality-cell-legend">
                {cellStats.map((s, idx) => (
                  <div key={idx} className="legend-item">
                    <span className="legend-dot" style={`background: ${s.color}`}></span>
                    <span className="legend-name">{s.name}</span>
                  </div>
                ))}
              </div>
              
              <table className="quality-stats-compact">
                <thead>
                  <tr>
                    <th>Cell</th>
                    <th>Absolute</th>
                    <th>Relative (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {cellStats.map((s, idx) => (
                    <tr key={idx} className={s.premiumLocked ? '' : getQualityRowClass(idx, bestCapLoss, worstCapLoss)}>
                      <td className="cell-name-col">{s.manufacturer}<br />{s.name}</td>
                      {s.premiumLocked ? (
                        <td className="metric-col" colSpan={2}>
                          <LockedMetricToken gated unit="Ah" category="quality" />
                        </td>
                      ) : (
                        <>
                          <td className="metric-col">
                            <span className="stat-value">{renderValue(s.capLoss, 'Ah', 4)}</span>
                          </td>
                          <td className="metric-col">
                            <span className="stat-value">
                              {renderPercent(s.capLossRel)}
                            </span>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Impedance Growth Section */}
            <div className="quality-metric-section">
              <div className="metric-header">
                <ImpedanceIcon size={14} />
                <span>Impedance Growth</span>
              </div>

              {/* Cell Legend */}
              <div className="quality-cell-legend">
                {cellStats.map((s, idx) => (
                  <div key={idx} className="legend-item">
                    <span className="legend-dot" style={`background: ${s.color}`}></span>
                    <span className="legend-name">{s.name}</span>
                  </div>
                ))}
              </div>
              
              <table className="quality-stats-compact">
                <thead>
                  <tr>
                    <th>Cell</th>
                    <th>Absolute</th>
                    <th>Relative (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {cellStats.map((s, idx) => (
                    <tr key={idx} className={s.premiumLocked ? '' : getQualityRowClass(idx, bestEisIncrease, worstEisIncrease)}>
                      <td className="cell-name-col">{s.manufacturer}<br />{s.name}</td>
                      {s.premiumLocked ? (
                        <td className="metric-col" colSpan={2}>
                          <LockedMetricToken gated unit="mΩ" category="quality" />
                        </td>
                      ) : (
                        <>
                          <td className="metric-col">
                            <span className="stat-value">{renderValue(s.eisIncrease, 'mΩ', 4)}</span>
                          </td>
                          <td className="metric-col">
                            <span className="stat-value">
                              {renderPercent(s.eisIncreaseRel, true)}
                            </span>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Back - Definition */}
          <div className="flipcard-back compare-flipcard-back">
            <button className="flip-btn flip-btn-back" onClick={() => toggleFlip('aging')} title="Close">×</button>
            <h3 className="card-title">{QUALITY.initialAging.title}</h3>
            <div className="flip-content">
              <p>{QUALITY.initialAging.intro.replace(/\{[^}]+\}/g, '')}</p>
              <h4>{QUALITY.initialAging.capacityFade.title}</h4>
              <p>{QUALITY.initialAging.capacityFade.text.replace(/\{[^}]+\}/g, '')}</p>
              <h4>{QUALITY.initialAging.impedanceGrowth.title}</h4>
              <p>{QUALITY.initialAging.impedanceGrowth.text.replace(/\{[^}]+\}/g, '')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
